import type { DocPerm } from './types';

/**
 * The location codec — one `AppLocation` model with two consumers: the URL and the presence
 * socket (Stage 1b, REACT-REWRITE-PLAN.md). PURE and DOM-free: the basename is a parameter,
 * never read from a global, and nothing here touches `history`, `S` or the store — the
 * history controller (Batch 2's `app/router.ts`) binds this to the live app.
 *
 * Path scheme (the rewrite plan's URL shape, verbatim):
 *
 *   /{locale}/                       map
 *   /{locale}/node/{nodeSlug}        node focused (popup open, map centred on it)
 *   /{locale}/doc/{docSlug}          doc overlay, view mode
 *   /{locale}/doc/{docSlug}/edit     doc overlay, edit mode
 *   /{locale}/admin/{screen}         permissions | dashboard | documents | projects | logs | ai-docs | targets | live-verify
 *   ?q=&scope=&type=&tag=&status=    search + filters, legal on any route
 *
 * ABSENT vs EMPTY — the one rule everything downstream depends on. A query param that is
 * OMITTED means "defer to the recipient's stored view state" and is `undefined` here; a param
 * that is PRESENT BUT EMPTY (`?tag=`) is a deliberate act — "clear this filter" — and is the
 * field's explicit clear value (`''` for q, `null` for the rest). `toUrl` writes the empty
 * param back out (`tag=`), so the distinction survives a round trip.
 *
 * THE LOCALE-MISSING RULE (chosen here, tested in location.test.ts): the first path segment
 * is taken as the locale only when it is not one of the reserved route words (`node`, `doc`,
 * `admin`) AND the remaining segments parse as a known route form. A path that starts with a
 * reserved word (`/doc/handbook`) parses with `locale: ''` — empty meaning "the URL named no
 * locale; substitute the default" (Batch 2's job, along with validating a non-empty locale
 * against the Language table — this codec extracts, it does not validate).
 *
 * TOLERANCE: an unrecognised path never throws — it parses to `{view:'map'}` plus a `problem`
 * flag (`unknown-path`, or `unknown-admin` for a structurally valid `/admin/…` naming no known
 * screen), keeping whatever locale the path did carry so the notice can render in the link's
 * language. Query parsing is independent of path parsing: a broken path with a good `?q=`
 * still yields the query. Unknown query params are ignored; a garbage VALUE in a validated
 * param (`?type=bogus`) is treated as absent — garbage must never clobber the recipient's own
 * stored state; only a deliberately empty param clears it.
 */

/* ------------------------------------------------------------------ vocabulary ---------- */

/** The eight admin screens that have a URL. Order is not meaningful. */
export const ADMIN_SCREENS = [
  'permissions',
  'dashboard',
  'documents',
  'projects',
  'logs',
  'ai-docs',
  'targets',
  'live-verify',
] as const;
export type AdminScreen = (typeof ADMIN_SCREENS)[number];

/**
 * The screens that are TABS of one region — the dashboard (V0.44.1; a fourth since V0.49, a
 * fifth since V0.50.2): `dashboard` is its Overview, `documents` the Documents table, `projects`
 * the Projects tab, `logs` the action log, and `ai-docs` the AI generation console. The tab key
 * IS the screen word, so there is no mapping table to keep in step, and switching between two
 * of them is a tab change, never a close-and-reopen (the router checks `isDashboardScreen` on
 * both sides before closing anything).
 *
 * `logs` MOVED INTO THIS LIST in V0.49. It was already an admin-screen word, pointing at a
 * standalone `#logsmodal` panel that no longer exists — the document log is a view inside the
 * Logs tab now, and `/admin/logs` opens the dashboard there. Every address anybody has shared
 * still resolves; only what it opens changed.
 *
 * `projects` (V0.50.2) is a shareable address like `logs`: it opens for `VIEW_TODOS` without
 * edit mode, so a team lead who never edits a page can be sent straight to a project.
 */
export const DASHBOARD_SCREENS = [
  'dashboard',
  'documents',
  'projects',
  'logs',
  'ai-docs',
] as const satisfies readonly AdminScreen[];
export type DashboardScreen = (typeof DASHBOARD_SCREENS)[number];
export const isDashboardScreen = (s: AdminScreen | null | undefined): s is DashboardScreen =>
  s !== null && s !== undefined && (DASHBOARD_SCREENS as readonly string[]).includes(s);

/**
 * The TABS of the permissions page (V0.54.5), each its own address:
 * `/admin/permissions/{tab}`. A sub-segment of `permissions` rather than four more screen
 * words beside the dashboard's, because they are one page with one gate to enter it — and
 * `users` or `backups` as a bare `/admin/{word}` would claim words far too general for that
 * namespace. `/admin/permissions` alone still parses (every link shared before this) and opens
 * the first tab the reader may see. The words are what the tabs SAY, not the component names.
 */
export const PERMISSION_TABS = ['users', 'roles', 'api-keys', 'backups'] as const;
export type PermissionTab = (typeof PERMISSION_TABS)[number];

/**
 * `?scope=` — which fields a search looks at. This is Phase 2's NEW vocabulary (plan 2.2:
 * name / desc / tags / content), used from day one so no minted link ever carries the old
 * presence-filter scope names. 2.2's reshaped `SearchScope` must share this constant.
 */
export const SEARCH_SCOPE_KEYS = ['name', 'desc', 'tags', 'content'] as const;
export type SearchScopeKey = (typeof SEARCH_SCOPE_KEYS)[number];

/**
 * `?type=` — narrow the doc-content search to one documentation kind. The five words are
 * `DocPerm`'s values verbatim and ON PURPOSE: a type filter is a doc-slot filter, gated by
 * the same `perm.docButtons` key. The `satisfies` plus the exported assertion type below make
 * drift in either direction a compile error — a changed vocabulary silently breaks every
 * link already minted.
 */
export const SEARCH_DOC_KINDS = [
  'documentation',
  'internal',
  'course',
  'demo',
  'custom',
] as const satisfies readonly DocPerm[];
export type SearchDocKind = (typeof SEARCH_DOC_KINDS)[number];

/**
 * `?heat=` — the map's heatmap mode, carrying mode+lens in one field (V0.39). One lens today,
 * `clearance`; a second (freshness, per the parked Batch B design) would be a new word here,
 * never a second param. Validated in the codec like `?type=`: an unknown value is treated as
 * absent, so garbage never clobbers the recipient's stored state.
 */
export const HEAT_LENSES = ['clearance'] as const;
export type HeatLens = (typeof HEAT_LENSES)[number];

type AssertTrue<T extends true> = T;
export type _EveryDocPermIsASearchKind = AssertTrue<DocPerm extends SearchDocKind ? true : false>;

/** Path segments that can never be a locale — they open a route form instead. */
const RESERVED_SEGMENTS = new Set<string>(['node', 'doc', 'admin']);

/* ------------------------------------------------------------------ the model ----------- */

export type AppView = 'map' | 'node' | 'doc' | 'admin';
export type DocMode = 'view' | 'edit';

export interface AppLocation {
  /**
   * The locale segment, taken as-is (see the header's locale-missing rule). `''` means the
   * URL named none; validation against the Language table is Batch 2's, not the codec's.
   */
  locale: string;
  view: AppView;
  /** view 'node' only: the node's frozen slug. */
  nodeSlug?: string;
  /** view 'doc' only: the doc page's slug. */
  docSlug?: string;
  /**
   * View or edit. URL-visible only for a doc (`…/edit`); parseUrl therefore always sets it
   * on a doc location and never on any other. A node location may carry it IN MEMORY (the
   * popup open in edit mode — presence reports it), but the URL never expresses it there.
   */
  mode?: DocMode;
  /** view 'admin' only: which screen. */
  admin?: AdminScreen;
  /** `admin: 'permissions'` only: which tab (V0.54.5). Absent = the first tab the reader may see. */
  adminTab?: PermissionTab;
  /** `?q=` search text. undefined = defer to stored state; `''` = explicitly clear it. */
  q?: string;
  /**
   * `?scope=` comma list. undefined = defer; null = present-but-empty (explicitly none);
   * otherwise a non-empty list — parseUrl never yields `[]` (an empty param is `null`, and
   * a param naming only unknown keys is treated as absent).
   */
  scope?: SearchScopeKey[] | null;
  /** `?type=` doc-kind filter. undefined = defer; null = explicitly clear; else one kind. */
  type?: SearchDocKind | null;
  /**
   * `?tag=` comma-separated multi-tag list (plan 2.3's multi-select, from day one).
   * undefined = defer; null = present-but-empty (clear the whole selection); otherwise a
   * non-empty list of tag ids — never `[]`. Ids are opaque here; whether each still exists
   * (or is tagHidden) is resolved against live state in Batch 2.
   */
  tag?: string[] | null;
  /**
   * `?status=` pipeline-stage filter (the PARAM keeps its historical name — minted URLs are
   * out in the world). undefined = defer; null = explicitly clear. The value is NOT validated
   * against the stage table here — it is data-adjacent (PIPELINE_DISPLAY keys), and the
   * router validates it where the table lives, dropping retired docStatus values as "off".
   */
  status?: string | null;
  /**
   * `?heat=` — the clearance heatmap (V0.39). undefined = defer to the recipient's stored
   * view state; null = present-but-empty (`?heat=`), the deliberate "off"; else one lens.
   */
  heat?: HeatLens | null;
}

export type ParseProblem = 'unknown-path' | 'unknown-admin';
export type ParsedLocation = AppLocation & { problem?: ParseProblem };

/* ------------------------------------------------------------------ helpers ------------- */

/** '' for the root ('' , '/', or any run of trailing slashes); '/x' shaped otherwise. */
function normalizeBasename(basename: string): string {
  const b = (basename || '').replace(/\/+$/g, '');
  if (!b) return '';
  return b.startsWith('/') ? b : `/${b}`;
}

/** Standard form decoding: '+' is a space, then percent-decode; malformed input kept raw. */
function formDecode(raw: string): string {
  const spaced = raw.replace(/\+/g, ' ');
  try {
    return decodeURIComponent(spaced);
  } catch {
    return spaced;
  }
}

/** Percent-decode one path segment; malformed input kept raw rather than thrown. */
function decodeSegment(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

const enc = encodeURIComponent;

/**
 * Join list elements with LITERAL commas: each element is fully encoded (so a comma inside
 * an element becomes %2C), the separators stay readable. `parseQuery` splits the RAW value
 * on ',' before decoding elements, which is what keeps the two unambiguous together.
 */
const encList = (items: readonly string[]): string => items.map(enc).join(',');

type Route =
  | { view: 'map' }
  | { view: 'node'; nodeSlug: string }
  | { view: 'doc'; docSlug: string; mode: DocMode }
  | { view: 'admin'; admin: AdminScreen; adminTab?: PermissionTab }
  | { view: 'map'; problem: 'unknown-admin' };

/** The route forms after the locale segment. null = not a known form. */
function parseRoute(segs: readonly string[]): Route | null {
  if (segs.length === 0) return { view: 'map' };
  if (segs[0] === 'node' && segs.length === 2) return { view: 'node', nodeSlug: segs[1] };
  if (segs[0] === 'doc' && segs.length === 2) return { view: 'doc', docSlug: segs[1], mode: 'view' };
  if (segs[0] === 'doc' && segs.length === 3 && segs[2] === 'edit') {
    return { view: 'doc', docSlug: segs[1], mode: 'edit' };
  }
  if (segs[0] === 'admin' && segs.length === 2) {
    const screen = ADMIN_SCREENS.find((s) => s === segs[1]);
    // Structurally an admin path, so the locale decision stands — only the screen is unknown.
    return screen ? { view: 'admin', admin: screen } : { view: 'map', problem: 'unknown-admin' };
  }
  if (segs[0] === 'admin' && segs.length === 3 && segs[1] === 'permissions') {
    const tab = PERMISSION_TABS.find((t) => t === segs[2]);
    // A permissions address naming no tab this app has is an unknown admin address, exactly as
    // an unknown screen word is — not silently the first tab, which would hide a broken link.
    return tab ? { view: 'admin', admin: 'permissions', adminTab: tab } : { view: 'map', problem: 'unknown-admin' };
  }
  return null;
}

type QueryFields = Pick<AppLocation, 'q' | 'scope' | 'type' | 'tag' | 'status' | 'heat'>;

/**
 * Split a raw comma list into decoded elements, dropping empty ones. `null` when nothing
 * survives — a present-but-empty list param is the explicit clear.
 */
function parseList(rawValue: string): string[] | null {
  const items = rawValue
    .split(',')
    .filter((el) => el !== '')
    .map(formDecode);
  return items.length ? items : null;
}

function parseQuery(search: string): QueryFields {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  // First occurrence of a key wins, matching URLSearchParams.get. Values stay RAW here so
  // list params can split on ',' before decoding (see encList).
  const rawByKey = new Map<string, string>();
  for (const pair of raw.split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    const key = formDecode(eq === -1 ? pair : pair.slice(0, eq));
    if (!rawByKey.has(key)) rawByKey.set(key, eq === -1 ? '' : pair.slice(eq + 1));
  }

  const out: QueryFields = {};

  const qRaw = rawByKey.get('q');
  if (qRaw !== undefined) out.q = formDecode(qRaw);

  const scopeRaw = rawByKey.get('scope');
  if (scopeRaw !== undefined) {
    const items = parseList(scopeRaw);
    if (items === null) {
      out.scope = null; // ?scope= — explicitly none
    } else {
      const known = items.filter((s): s is SearchScopeKey =>
        (SEARCH_SCOPE_KEYS as readonly string[]).includes(s),
      );
      // Only-garbage is treated as absent, not as a clear: see the header's tolerance rule.
      if (known.length) out.scope = known;
    }
  }

  const typeRaw = rawByKey.get('type');
  if (typeRaw !== undefined) {
    if (typeRaw === '') {
      out.type = null; // ?type= — explicitly no kind filter
    } else {
      const decoded = formDecode(typeRaw);
      const kind = SEARCH_DOC_KINDS.find((k) => k === decoded);
      if (kind) out.type = kind; // unknown kind: absent, never a clear
    }
  }

  const tagRaw = rawByKey.get('tag');
  if (tagRaw !== undefined) out.tag = parseList(tagRaw); // null = ?tag= — clear the selection

  const statusRaw = rawByKey.get('status');
  if (statusRaw !== undefined) out.status = statusRaw === '' ? null : formDecode(statusRaw);

  const heatRaw = rawByKey.get('heat');
  if (heatRaw !== undefined) {
    if (heatRaw === '') {
      out.heat = null; // ?heat= — explicitly off
    } else {
      const decoded = formDecode(heatRaw);
      const lens = HEAT_LENSES.find((k) => k === decoded);
      if (lens) out.heat = lens; // unknown lens: absent, never a clear
    }
  }

  return out;
}

/* ------------------------------------------------------------------ parse / build ------- */

/**
 * URL -> location. Never throws; see the header for the locale-missing rule and what the
 * `problem` flag means. `search` may arrive with or without its leading '?'.
 */
export function parseUrl(pathname: string, search: string, basename: string): ParsedLocation {
  const base = normalizeBasename(basename);
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const query = parseQuery(search);

  let rest: string | null = null;
  if (!base) rest = path;
  else if (path === base) rest = '/';
  else if (path.startsWith(`${base}/`)) rest = path.slice(base.length);
  if (rest === null) {
    // Outside the mount entirely — no locale to salvage.
    return { locale: '', view: 'map', problem: 'unknown-path', ...query };
  }

  const segments = rest.split('/').filter(Boolean).map(decodeSegment);

  // The bare mount root is a legitimate entry URL (today's only URL), not a problem: it means
  // "no locale chosen yet", and Batch 2 substitutes the default and replaceStates.
  if (segments.length === 0) return { locale: '', view: 'map', ...query };

  let locale: string;
  let route: Route | null;
  if (RESERVED_SEGMENTS.has(segments[0])) {
    // A reserved word can never be a locale: the whole path is a locale-less route form.
    locale = '';
    route = parseRoute(segments);
  } else {
    locale = segments[0];
    route = parseRoute(segments.slice(1));
  }

  if (!route) return { locale, view: 'map', problem: 'unknown-path', ...query };
  return { locale, ...route, ...query };
}

/**
 * Location -> URL (path + query), the inverse of `parseUrl` — property-tested both ways.
 * Omits absent (undefined) params; writes explicit-clear values back as bare params
 * (`?tag=`), which is what keeps absent-vs-empty a property of the URL rather than of one
 * process's memory. An empty list is written as its param's clear form, same as null.
 */
export function toUrl(loc: AppLocation, basename: string): string {
  const base = normalizeBasename(basename);

  const parts: string[] = [];
  if (loc.locale) parts.push(enc(loc.locale));
  if (loc.view === 'node' && loc.nodeSlug) {
    parts.push('node', enc(loc.nodeSlug));
  } else if (loc.view === 'doc' && loc.docSlug) {
    parts.push('doc', enc(loc.docSlug));
    if (loc.mode === 'edit') parts.push('edit');
  } else if (loc.view === 'admin' && loc.admin) {
    parts.push('admin', enc(loc.admin));
    if (loc.admin === 'permissions' && loc.adminTab) parts.push(enc(loc.adminTab));
  }
  // A node/doc/admin location missing its slug/screen degrades to the map form above —
  // toUrl is total rather than throwing over a half-built location.

  const isMapShape = parts.length === (loc.locale ? 1 : 0);
  // The map form carries a trailing slash (the plan writes it `/{locale}/`); deeper forms
  // do not. The parser treats both identically either way.
  const path = parts.length ? `${base}/${parts.join('/')}${isMapShape ? '/' : ''}` : `${base}/`;

  const qs: string[] = [];
  if (loc.q !== undefined) qs.push(`q=${enc(loc.q)}`);
  if (loc.scope !== undefined) {
    qs.push(loc.scope === null || loc.scope.length === 0 ? 'scope=' : `scope=${encList(loc.scope)}`);
  }
  if (loc.type !== undefined) qs.push(loc.type === null ? 'type=' : `type=${enc(loc.type)}`);
  if (loc.tag !== undefined) {
    qs.push(loc.tag === null || loc.tag.length === 0 ? 'tag=' : `tag=${encList(loc.tag)}`);
  }
  if (loc.status !== undefined) qs.push(loc.status === null ? 'status=' : `status=${enc(loc.status)}`);
  if (loc.heat !== undefined) qs.push(loc.heat === null ? 'heat=' : `heat=${enc(loc.heat)}`);

  return qs.length ? `${path}?${qs.join('&')}` : path;
}

/* ------------------------------------------------------------------ presence ------------ */

/**
 * Structurally identical to `app/presence.ts`'s `PresenceLoc` — restated here because this
 * module may not import from app/ (it must stay pure). Batch 2 rewrites `myPresenceLoc()`
 * as `toPresenceLoc(currentAppLocation(), …)`, and structural typing is what proves the two
 * shapes still agree: the rewrite fails to compile the day they drift.
 */
export interface PresenceLocation {
  view: 'map' | 'doc';
  nodeId: string | null;
  docId: string | null;
  mode: DocMode;
}

/**
 * The URL carries SLUGS; the presence socket speaks IDS. Resolution needs live state (the
 * loaded map, `docSlugs` from /api/map), so the caller hands in the resolver and this stays
 * pure — Batch 2 binds it.
 */
export interface SlugResolver {
  nodeIdBySlug(slug: string): string | null;
  docIdBySlug(slug: string): string | null;
}

/**
 * Location -> what the presence socket reports, mirroring `myPresenceLoc()`'s cases:
 *
 * - doc:   view 'doc' with the resolved doc id. The URL never carries the doc's owning nav
 *   node, so a parsed location reports `nodeId: null`; an IN-MEMORY location that knows it
 *   (Batch 2 sets `nodeSlug` from `docNavNodeId`) has it resolved — which is what keeps
 *   today's behaviour once `myPresenceLoc()` is rewritten on top of this.
 * - node:  presence has no 'node' view — a focused node IS the map with a popup, so this is
 *   view 'map' plus the resolved node id, `mode` passed through ('view' when unset).
 * - map:   `{view:'map', nodeId:null, docId:null, mode:'view'}` — the plain-map branch
 *   always reports 'view', exactly as `myPresenceLoc()` does.
 * - admin: presence has no admin vocabulary (an open admin screen reports as the map today);
 *   same as map.
 *
 * A doc slug that does not resolve falls back to the plain map: broadcasting "in a document"
 * with no document would be meaningless to every consumer that looks the title up by id.
 */
export function toPresenceLoc(loc: AppLocation, resolve: SlugResolver): PresenceLocation {
  if (loc.view === 'doc' && loc.docSlug) {
    const docId = resolve.docIdBySlug(loc.docSlug);
    if (docId) {
      return {
        view: 'doc',
        nodeId: loc.nodeSlug ? resolve.nodeIdBySlug(loc.nodeSlug) : null,
        docId,
        mode: loc.mode === 'edit' ? 'edit' : 'view',
      };
    }
    return { view: 'map', nodeId: null, docId: null, mode: 'view' };
  }
  if (loc.view === 'node' && loc.nodeSlug) {
    return {
      view: 'map',
      nodeId: resolve.nodeIdBySlug(loc.nodeSlug),
      docId: null,
      mode: loc.mode === 'edit' ? 'edit' : 'view',
    };
  }
  return { view: 'map', nodeId: null, docId: null, mode: 'view' };
}
